import { createOrganizationApi, updateOrganizationApi } from "@/api/services/admin/organization";
import { FooterActionBar as StickerFooter } from "@/modules/irohana-components/elements/FooterActionBar";
import { Button } from "@/modules/irohana-components/elements/Button";
import { Form } from "@/modules/irohana-components/elements/Form";
import { MenuNavigate } from "@/modules/irohana-components/elements/MenuNavigate";
import { TopPage } from "@/modules/irohana-components/elements/TopPage";
import { API_SUCCESS_MESSAGE } from "@/constants/common";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { BasicForm } from "@/features/organization/components/Form/BasicForm";
import { ContractForm } from "@/features/organization/components/Form/ContractForm";
import { OtherInfoForm } from "@/features/organization/components/Form/OtherInfoForm";
import { schema } from "@/features/organization/schema/schema";
import { TOrganizationForm } from "@/features/organization/types/organizationForm";
import { convertOrganizationFormToCreateReq, convertOrganizationFormToUpdateReq } from "@/features/organization/utils/converts";
import { useToast } from "@/hooks/useToast";
import { useTriggerValidate } from "@/hooks/useTriggerValidate";
import { AppRoutes } from "@/routes/config";
import { setErrorForm, showError } from "@/utils/error";
import { getDynamicRoute } from "@/utils/url";
import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Stack, styled } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import Cookies from "js-cookie";
import { FC, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";

type TOrganizationFormProps = {
  previousData?: Partial<TOrganizationForm>;
  backUrl: string;
  // Create mode only: irohana-center identifiers from the SSO handoff (see OrganizationCreate page).
  centerOrganizationId?: number;
  productContractId?: number;
};

const initForm: TOrganizationForm = {
  id: null,
  name: "",
  contractStatus: "",
  contractPlan: "",
  note: "",
  trialStartDate: null,
  trialEndDate: null,
  contractStartDate: null,
  contractEndDate: null,
};

export const OrganizationForm: FC<TOrganizationFormProps> = ({ previousData, backUrl, centerOrganizationId, productContractId }) => {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(false);

  const initialValues = previousData ?? initForm;

  const methods = useForm<TOrganizationForm, object, TOrganizationForm>({
    defaultValues: initialValues,
    resolver: yupResolver<TOrganizationForm>(schema),
    mode: "all",
    reValidateMode: "onBlur",
  });

  useTriggerValidate({ methods, name: "contractStatus", triggerFields: ["trialEndDate", "contractStartDate"] });
  useTriggerValidate({ methods, name: "contractStartDate", triggerFields: ["contractStatus", "trialEndDate", "contractEndDate"] });
  useTriggerValidate({ methods, name: "trialStartDate", triggerFields: ["trialEndDate", "contractStartDate", "contractStatus"] });
  useTriggerValidate({ methods, name: "trialEndDate", triggerFields: ["trialStartDate", "contractStartDate", "contractStatus"] });
  useTriggerValidate({ methods, name: "contractEndDate", triggerFields: ["contractStatus"] });

  const onSubmit = async (data: TOrganizationForm) => {
    // Create mode requires the center identifiers from the SSO handoff; bail if missing.
    if (!isEdit && (centerOrganizationId == null || productContractId == null)) return;

    setLoading(true);
    try {
      if (isEdit) {
        const payload = convertOrganizationFormToUpdateReq(data);
        const rs = await updateOrganizationApi(id!, payload);
        toast.success(rs.message ?? API_SUCCESS_MESSAGE);
        await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ORGANIZATION.FETCH_DETAIL, id] });
        await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ORGANIZATION.FETCH_LIST] });
        navigate(backUrl || AppRoutes.organizationList);
      } else {
        const payload = convertOrganizationFormToCreateReq(data, {
          centerOrganizationId: centerOrganizationId!,
          productContractId: productContractId!,
        });
        const rs = await createOrganizationApi(payload);
        toast.success(rs.message ?? API_SUCCESS_MESSAGE);
        Cookies.remove("contract-token");
        await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ORGANIZATION.FETCH_LIST] });
        navigate(getDynamicRoute(AppRoutes.organizationDetail, { id: rs.data.id }));
      }
    } catch (error) {
      showError(error);
      setErrorForm(methods, error);
    }
    setLoading(false);
  };

  return (
    <PageWrapper sx={{ mt: 1 }}>
      <Box sx={{ mb: 2.5 }}>
        <Form methods={methods} onSubmit={onSubmit} schema={schema}>
          <TopPage title={isEdit ? "組織情報の編集" : "組織新規登録"} enablePageBack={false} />
          <Box mt="36px">
            <MenuNavigate
              contents={[
                { title: "基本情報", content: <BasicForm /> },
                { title: "契約情報", content: <ContractForm /> },
                { title: "その他情報", content: <OtherInfoForm /> },
              ]}
              contentAffix={
                <StickerFooter sx={{ mt: 4 }}>
                  <Stack gap={1} direction="row" justifyContent="space-between" alignItems="center">
                    <Button size="sm" variant="secondary" onClick={() => navigate(backUrl || AppRoutes.organizationList)}>
                      戻る
                    </Button>
                    <Button disabled={loading} size="sm" type="submit">
                      保存
                    </Button>
                  </Stack>
                </StickerFooter>
              }
            />
          </Box>
        </Form>
      </Box>
    </PageWrapper>
  );
};

const PageWrapper = styled(Stack)`
  width: 1198px;
  margin: 0 auto;
`;
